import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Toolbar,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { CmpButtonWithIcon, CmpTypographyField } from '../../../component/ToolBox/ToolBox';
import { exportToExcel, fAlertToast } from '../../../Utility/Utilitys';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { DataGridPro } from '@mui/x-data-grid-pro';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { stockTransferPackingUpdate } from '../actions';
import { useSelector } from 'react-redux';

const toolbar = {
  backgroundColor: '#0d73bb',
  color: 'white',
  display: 'flex',
  justifyContent: 'space-between',
  minHeight: '40px !important'
};

export const RequestPackQtySelection = ({
  data,
  open,
  onClose,
  subDataGrid,
  setSubDataGrid,
  calculate_PackQty,
  calculate_Qty,
  title,
  sCurrentRowSelected
}) => {
  const vMediaQuery = useMediaQuery('max-width:600px');
  const rdxHeadObj = useSelector((state) => state.OutletRequestSlice.value.transferHead);
  const to2 = (n) => Number(Number(n).toFixed(2));

  const updateValues = (newRow, oldRow = {}) => {
    const qtyRaw = newRow.Quantity ?? oldRow.Quantity ?? '';
    const qtyNum = qtyRaw === '' ? 0 : Number(qtyRaw);

    const packQtyRaw = newRow.PackQuantity ?? oldRow.PackQuantity ?? '';
    const packQtyNum = packQtyRaw === '' ? 0 : Number(packQtyRaw);

    return {
      ...oldRow,
      ...newRow,
      Quantity: qtyRaw,
      PackQuantity: packQtyRaw,
      BaseQuantity: to2(qtyNum * packQtyNum)
    };
  };

  const setQuantity = (params) => {
    const { row, value } = params;
    const nextRow = updateValues({ ...row, Quantity: value }, row);
    const updatedRows = subDataGrid.map((itm) => (itm.id === nextRow.id ? nextRow : itm));
    setSubDataGrid(updatedRows);
    return nextRow;
  };
  const setPackQuantity = (params) => {
    const { row, value } = params;
    const nextRow = updateValues({ ...row, PackQuantity: value }, row);
    const updatedRows = subDataGrid.map((itm) => (itm.id === nextRow.id ? nextRow : itm));
    setSubDataGrid(updatedRows);
    return nextRow;
  };

  //focus the edit field one by one
  const fieldToIdPrefix = (field) => (field === 'Quantity' ? 'Quantity' : 'PackQuantity');
  const idFor = (field, rowIdx) => `${fieldToIdPrefix(field)}-${rowIdx}`;

  const focusCellByIndex = (api, rowIndex, field) => {
    if (rowIndex < 0) return;
    // Bring target row into view first
    api.scrollToIndexes({ rowIndex });

    // Focus the specific input by ID after the row renders
    const targetId = idFor(field, rowIndex);
    // allow the grid to render the row, sometimes two frames helps with virtualization
    requestAnimationFrame(() => {
      const el1 = document.getElementById(targetId);
      if (el1) {
        el1.focus();
        return;
      }
      requestAnimationFrame(() => {
        document.getElementById(targetId)?.focus();
      });
    });
  };

  // focus to edit field each row in horizontally  when 0 it changed '' Optional: reverse on Shift+Enter
  const isShiftEnter = (e) => e.key === 'Enter' && e.shiftKey;
  const isEnterOnly = (e) => e.key === 'Enter' && !e.shiftKey;

  const isZeroish = (v) => {
    const s = String(v ?? '').trim();
    if (s === '') return false;
    const n = Number(s);
    return !Number.isNaN(n) && n === 0;
  };

  const fContent_Focus = (params, e, setSubDataGrid) => {
    // params.id -> row id, params.field -> column field
    const { id, field } = params;
    const raw = e.target.value;

    if (!isZeroish(raw)) return;

    setSubDataGrid((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: '' } : r)));
  };

  // blur to edit field each row in horizontally  when ' ' it changed 0
  const toFixedNum = (n, d = 2) => Number(Number(n).toFixed(d));
  const toNumOr0 = (s) => {
    const t = String(s ?? '').trim();
    if (t === '') return 0;
    const n = Number(t);
    return Number.isFinite(n) ? n : 0;
  };

  // params-based blur (recommended)
  const fContent_Blur = (params, e, setSubDataGrid, { decimals = 2, min = 0 } = {}) => {
    const { id, field, row } = params;
    const raw = e.target.value;
    const num = toNumOr0(raw);
    const clamped = Math.max(min, num);
    const committed = decimals == null ? clamped : toFixedNum(clamped, decimals);

    setSubDataGrid((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const next = { ...r, [field]: committed };
        // If you have updateValues(newRow, oldRow), keep derived fields in sync:
        return typeof updateValues === 'function' ? updateValues(next, r) : next;
      })
    );
  };

  const validateRow = (row) => {
    const qty = Number(row.Quantity ?? row.Quantity ?? 0);
    const packQty = Number(row.PackQuantity ?? 0);

    const errors = [];

    if (qty !== 0 && packQty <= 0) {
      errors.push('PackQty must be > 0 when Qty ≠ 0');
    }

    if (packQty !== 0 && qty <= 0) {
      errors.push('Qty must be > 0 when PackQty ≠ 0');
    }

    return { valid: errors.length === 0, errors, qty, packQty };
  };

  const validateRowsById = (rows) => {
    const errorsById = {};
    for (const r of rows) {
      const { valid, errors } = validateRow(r);
      if (!valid) errorsById[r.id] = errors;
    }
    return {
      valid: Object.keys(errorsById).length === 0,
      errorsById
    };
  };

  const fAddItem = () => {
    const newItem = {
      ItemCode: data?.IL_ItmCd,
      SerialNo: subDataGrid?.length + 1,
      Quantity: 0,
      PackQuantity: 0,
      BaseQuantity: 0,
      Type: 'MK',
      id: subDataGrid?.length + 1
    };
    setSubDataGrid([...subDataGrid, newItem]);
  };

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 140,
      hide: true
    },
    {
      field: 'SerialNo',
      headerName: 'SerialNo',
      width: 140,
      hide: true
    },
    {
      field: 'ItemCode',
      headerName: 'Item Code',
      width: 170,
      hide: true
    },
    {
      field: 'Type',
      headerName: 'Type',
      width: vMediaQuery ? 100 : 120,
      type: 'number',
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: 'Quantity',
      headerName: vMediaQuery ? 'Qty' : 'Quantity',
      width: vMediaQuery ? 70 : 130,
      type: 'number',
      align: 'center',
      headerAlign: 'center',
      cellClassName: 'editable-cell',
      renderCell: (params) => {
        const { row } = params;
        if (row.isSummaryRow) return <span>{params.value}</span>;
        const rowIdx = params.api.getRowIndexRelativeToVisibleRows(params.id);
        const myId = idFor('Quantity', rowIdx);
        return (
          <TextField
            id={myId}
            name={params.field}
            value={row?.Quantity ?? ''} // show whatever is in state ('' allowed)
            onKeyDown={(e) => {
              // stop grid arrow nav
              if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key))
                e.stopPropagation();
              // block minus / exponent
              if (e.key === '-' || e.key.toLowerCase() === 'e') e.preventDefault();

              // Enter -> focus same-row Pieces
              if (isEnterOnly(e)) {
                e.preventDefault();
                e.stopPropagation();
                focusCellByIndex(params.api, rowIdx, 'PackQuantity');
              }
              // Shift+Enter -> go to previous row's Pieces (optional)
              if (isShiftEnter(e)) {
                e.preventDefault();
                e.stopPropagation();
                focusCellByIndex(params.api, rowIdx - 1, 'PackQuantity');
              }
            }}
            onChange={(e) => {
              const v = e.target.value;
              // allow '' while typing; otherwise keep raw text and sanitize later
              if (v === '') setQuantity({ ...params, value: '' });
              else setQuantity({ ...params, value: v });
            }}
            onFocus={(e) => fContent_Focus(params, e, setSubDataGrid)}
            onBlur={(e) => fContent_Blur(params, e, setSubDataGrid, { decimals: 2, min: 0 })}
            disabled={
              rdxHeadObj?.status === 'POST' || data?.isAllowedMultiPacking === false
                ? true
                : row?.Type === 'MK'
                ? false
                : true
            }
            autoComplete="off"
            variant="outlined"
            size="small"
            inputProps={{
              inputMode: 'decimal',
              type: 'text', // <-- important: allow empty/backspace
              pattern: '[0-9]*[.]?[0-9]*',
              style: { textAlign: 'center' }
            }}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-input': {
                textAlign: 'right',
                padding: '4px 12px',
                fontSize: '13px',
                backgroundColor: '#edfaff',
                // border: '1px solid lightgray',
                borderRadius: '6px !important'
              },
              '& .MuiOutlinedInput-input.Mui-disabled': {
                backgroundColor: '#e9ecef !important',
                WebkitTextFillColor: 'GrayText !important'
              }
            }}
          />
        );
      }
    },
    {
      field: 'PackQuantity',
      headerName: 'Pack Qty',
      width: vMediaQuery ? 70 : 130,
      type: 'number',
      align: 'center',
      headerAlign: 'center',
      editable: false,
      renderCell: (params) => {
        const { row } = params;
        if (row.isSummaryRow) return <span>{params.value}</span>;
        const rowIdx = params.api.getRowIndexRelativeToVisibleRows(params.id);
        const myId = idFor('PackQuantity', rowIdx);
        return (
          <TextField
            id={myId}
            name={params.field}
            value={row?.PackQuantity ?? ''} // show whatever is in state ('' allowed)
            onKeyDown={(e) => {
              // stop grid arrow nav
              if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key))
                e.stopPropagation();
              // block minus / exponent
              if (e.key === '-' || e.key.toLowerCase() === 'e') e.preventDefault();

              // Enter -> focus same-row Pieces
              if (isEnterOnly(e)) {
                e.preventDefault();
                e.stopPropagation();
                focusCellByIndex(params.api, rowIdx, 'Quantity');
              }
              // Shift+Enter -> go to previous row's Pieces (optional)
              if (isShiftEnter(e)) {
                e.preventDefault();
                e.stopPropagation();
                focusCellByIndex(params.api, rowIdx - 1, 'Quantity');
              }
            }}
            onChange={(e) => {
              const v = e.target.value;
              // allow '' while typing; otherwise keep raw text and sanitize later
              if (v === '') setPackQuantity({ ...params, value: '' });
              else setPackQuantity({ ...params, value: v });
            }}
            onFocus={(e) => fContent_Focus(params, e, setSubDataGrid)}
            onBlur={(e) => fContent_Blur(params, e, setSubDataGrid, { decimals: 2, min: 0 })}
            disabled={
              rdxHeadObj?.status === 'POST' || data?.isAllowedMultiPacking === false
                ? true
                : row?.Type === 'MK'
                ? false
                : true
            }
            autoComplete="off"
            variant="outlined"
            size="small"
            inputProps={{
              inputMode: 'decimal',
              type: 'text', // <-- important: allow empty/backspace
              pattern: '[0-9]*[.]?[0-9]*',
              style: { textAlign: 'center' }
            }}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-input': {
                textAlign: 'right',
                padding: '4px 12px',
                fontSize: '13px',
                backgroundColor: '#edfaff',
                // border: '1px solid lightgray',
                borderRadius: '6px !important'
              },
              '& .MuiOutlinedInput-input.Mui-disabled': {
                backgroundColor: '#e9ecef !important',
                WebkitTextFillColor: 'GrayText !important'
              }
            }}
          />
        );
      }
    },
    {
      field: 'BaseQuantity',
      headerName: 'Base Qty',
      width: vMediaQuery ? 100 : 150,
      type: 'number',
      align: 'center',
      headerAlign: 'center'
    }
  ];

  const fSaveApi = async () => {
    const iArray = subDataGrid?.map((itm) => ({
      ItemCode: itm.ItemCode,
      SerialNo: itm.SerialNo,
      Quantity: itm.Quantity,
      PackQuantity: itm.PackQuantity,
      BaseQuantity: itm.BaseQuantity,
      Type: itm.Type === 'MK' ? 0 : 1
    }));
    const obj = {
      vocTyp: rdxHeadObj?.vocTyp,
      vocNo: rdxHeadObj?.vocNo,
      loc: rdxHeadObj?.loc,
      detail: iArray
    };
    const result = await stockTransferPackingUpdate(obj);
    console.log('res', result);
    if (result.Type === 'Success') {
      fAlertToast('SUCCESS', 'PackQty selection saved successfully');

      onClose();
    } else {
      fAlertToast('FAILED', 'PackQty selection not saved');
    }
  };

  const fApply = () => {
    // validate every row (or filter to updated ones if you prefer)
    const { valid, errorsById } = validateRowsById(subDataGrid);

    if (!valid) {
      const [firstId, msgs] = Object.entries(errorsById)[0];
      fAlertToast('FAILED', `Row ID ${firstId}: ${msgs[0]}`);
      return;
    }

    let PackQty = 0;
    if (totals?.BaseQuantity && totals.Quantity !== 0) {
      PackQty = totals.BaseQuantity / totals.Quantity;
    }

    console.log(
      'data:',
      sCurrentRowSelected,
      PackQty.toFixed(3),
      totals.Quantity.toFixed(4),
      false
    );

    calculate_PackQty(sCurrentRowSelected, PackQty.toFixed(3), totals.Quantity.toFixed(2), false);
    calculate_Qty(sCurrentRowSelected, totals.Quantity.toFixed(2), PackQty.toFixed(3));

    fSaveApi();
  };

  const handleExport = () => {
    exportToExcel(subDataGrid, 'Packing_data', 'Packing');
  };

  const totals = React.useMemo(() => {
    return subDataGrid?.reduce(
      (a, r) => ({
        Quantity: a.Quantity + Number(r.Quantity || 0),
        PackQuantity: a.PackQuantity + Number(r.PackQuantity || 0),
        BaseQuantity: a.BaseQuantity + Number(r.BaseQuantity || 0)
      }),
      {
        Quantity: 0,
        PackQuantity: 0,
        BaseQuantity: 0
      }
    );
  }, [subDataGrid]);

  return (
    <Dialog open={open} maxWidth="sm">
      <Toolbar sx={toolbar}>
        <DialogTitle
          sx={{
            backgroundColor: '#0d73bb',
            color: 'white',
            padding: '0 4px !important'
          }}>
          {title}
        </DialogTitle>
        <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </Toolbar>

      <DialogContent
        sx={{
          '&.MuiDialogContent-root': {
            padding: vMediaQuery ? '10px !important' : '16px !important'
          }
        }}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={12} md={12} lg={12} textAlign={'center'}>
            <CmpTypographyField xVariant="h3" xText={`${data?.IL_ItmCd} - ${data?.ItemName}`} />
          </Grid>
          <Grid item xs={12} sm={5} md={5} lg={5}>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Grid
                container
                spacing={1}
                columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}
                sx={{ alignItems: 'center' }}>
                <Grid item xs={4} sm={3.5} lg={3.5} md={3.5}>
                  <CmpTypographyField xcomponent={'span'} xText={'Pack Qty'} />
                </Grid>
                <Grid item xs={1} sm={1} lg={1} md={1}>
                  <CmpTypographyField xcomponent={'span'} xText={':'} />
                </Grid>
                <Grid item xs={7} sm={7.5} lg={7.5} md={7.5}>
                  <CmpTypographyField xcomponent={'span'} xText={data?.B_Tsr_PackQty} />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Grid
                container
                spacing={1}
                columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}
                sx={{ alignItems: 'center' }}>
                <Grid item xs={4} sm={3.5} lg={3.5} md={3.5}>
                  <CmpTypographyField xcomponent={'span'} xText={'Stock'} />
                </Grid>
                <Grid item xs={1} sm={1} lg={1} md={1}>
                  <CmpTypographyField xcomponent={'span'} xText={':'} />
                </Grid>
                <Grid item xs={7} sm={7.5} lg={7.5} md={7.5}>
                  <CmpTypographyField xcomponent={'span'} xText={Math.round(data?.IL_StockCTN)} />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Grid
                container
                spacing={1}
                columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}
                sx={{ alignItems: 'center' }}>
                <Grid item xs={4} sm={3.5} lg={3.5} md={3.5}>
                  <CmpTypographyField xcomponent={'span'} xText={'DC Pick'} />
                </Grid>
                <Grid item xs={1} sm={1} lg={1} md={1}>
                  <CmpTypographyField xcomponent={'span'} xText={':'} />
                </Grid>
                <Grid item xs={7} sm={7.5} lg={7.5} md={7.5}>
                  <CmpTypographyField xcomponent={'span'} xText={data?.DCPickShowing} />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Grid
                container
                spacing={1}
                columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}
                sx={{ alignItems: 'center' }}>
                <Grid item xs={4} sm={3.5} lg={3.5} md={3.5}>
                  <CmpTypographyField xcomponent={'span'} xText={'Req Qty'} />
                </Grid>
                <Grid item xs={1} sm={1} lg={1} md={1}>
                  <CmpTypographyField xcomponent={'span'} xText={':'} />
                </Grid>
                <Grid item xs={7} sm={7.5} lg={7.5} md={7.5}>
                  <CmpTypographyField
                    xcomponent={'span'}
                    xText={Math.round(data?.IL_FinalQtyCTN)}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Grid
                container
                spacing={1}
                columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}
                sx={{ alignItems: 'center' }}>
                <Grid item xs={4} sm={3.5} lg={3.5} md={3.5}>
                  <CmpTypographyField xcomponent={'span'} xText={'Suggest'} />
                </Grid>
                <Grid item xs={1} sm={1} lg={1} md={1}>
                  <CmpTypographyField xcomponent={'span'} xText={':'} />
                </Grid>
                <Grid item xs={7} sm={7.5} lg={7.5} md={7.5}>
                  <CmpTypographyField xcomponent={'span'} xText={data?.SuggestedTransferCTN} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={7} md={7} lg={7}>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Grid
                container
                spacing={1}
                columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}
                sx={{ alignItems: 'center' }}>
                <Grid item xs={4} sm={2.5} lg={2.5} md={2.5}>
                  <CmpTypographyField xcomponent={'div'} xText={'Voc.Type'} />
                </Grid>
                <Grid item xs={1} sm={0.5} lg={0.5} md={0.5}>
                  <CmpTypographyField xcomponent={'div'} xText={':'} />
                </Grid>
                <Grid item xs={7} sm={9} lg={9} md={9}>
                  <CmpTypographyField
                    xcomponent={'span'}
                    xText={`${rdxHeadObj?.vocTyp} - ${rdxHeadObj?.vocName}`}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Grid
                container
                spacing={1}
                columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}
                sx={{ alignItems: 'center' }}>
                <Grid item xs={4} sm={2.5} lg={2.5} md={2.5}>
                  <CmpTypographyField xcomponent={'span'} xText={'Voc. No'} />
                </Grid>
                <Grid item xs={1} sm={0.5} lg={0.5} md={0.5}>
                  <CmpTypographyField xcomponent={'span'} xText={':'} />
                </Grid>
                <Grid item xs={7} sm={9} lg={9} md={9}>
                  <CmpTypographyField xcomponent={'span'} xText={rdxHeadObj?.vocNo} />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Grid
                container
                spacing={1}
                columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}
                sx={{ alignItems: 'center' }}>
                <Grid item xs={4} sm={2.5} lg={2.5} md={2.5}>
                  <CmpTypographyField xcomponent={'span'} xText={'Branch'} />
                </Grid>
                <Grid item xs={1} sm={0.5} lg={0.5} md={0.5}>
                  <CmpTypographyField xcomponent={'span'} xText={':'} />
                </Grid>
                <Grid item xs={7} sm={9} lg={9} md={9}>
                  <CmpTypographyField
                    xcomponent={'span'}
                    xText={`${rdxHeadObj?.loc} - ${rdxHeadObj?.locName}`}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Grid
                container
                spacing={1}
                columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}
                sx={{ alignItems: 'center' }}>
                <Grid item xs={6} sm={6.5} lg={6.5} md={6.5}></Grid>
                <Grid item xs={5} sm={4.5} lg={4.5} md={4.5}>
                  <CmpButtonWithIcon
                    xvariant={'contained'}
                    xsize={'small'}
                    xonClick={fAddItem}
                    xDisable={
                      rdxHeadObj?.status === 'POST' || data?.isAllowedMultiPacking === false
                        ? true
                        : // : row?.Type === 'MK'
                          // ? false
                          false
                    }
                    xLabel={'Add Item'}
                    xIcon={<AddCircleIcon />}
                  />
                </Grid>
                <Grid item xs={1} sm={1} lg={1} md={1}>
                  <Tooltip title="Export">
                    <IconButton
                      color="primary"
                      onClick={handleExport}
                      size="small"
                      sx={{
                        padding: 0,
                        '& .MuiSvgIcon-root': {
                          fontSize: '20px'
                        }
                      }}>
                      <DownloadOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Box
              sx={{
                height: '55vh',
                width: '100%'
              }}>
              <DataGridPro
                className="gridTable"
                rowHeight={30}
                headerHeight={45}
                rows={subDataGrid}
                columns={columns}
                getRowId={(row) => row.id}
                pinnedRows={{
                  bottom: [
                    {
                      id: 'footer',
                      SerialNo: 'TOTAL',
                      Quantity: totals.Quantity.toFixed(2),
                      PackQuantity: totals.PackQuantity.toFixed(2),
                      BaseQuantity: totals.BaseQuantity.toFixed(2),
                      isSummaryRow: true
                    }
                  ]
                }}
                initialState={{
                  columns: {
                    columnVisibilityModel: {
                      id: false,
                      SerialNo: false,
                      ItemCode: false
                    }
                  }
                }}
                disableRowSelectionOnClick
                isCellEditable={(params) => !params.row?.isSummaryRow}
                getRowClassName={(params) => (params.row?.isSummaryRow ? 'summary-row' : '')}
                isRowSelectable={(params) => !params.row?.isSummaryRow}
                hideFooter
              />
            </Box>

            <Box textAlign={'center'}>
              <Button
                onClick={fApply}
                variant="contained"
                size="small"
                sx={{ m: 1 }}
                //disabled={rdxHeadObj?.status === 'POST'}
                disabled={
                  rdxHeadObj?.status === 'POST' || data?.isAllowedMultiPacking === false
                    ? true
                    : // : row?.Type === 'MK'
                      // ? false
                      false
                }>
                Apply
              </Button>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
